import {
  Host,
  Component,
  Element,
  Method,
  Listen,
  Prop,
  Event,
  EventEmitter,
  h,
} from "@stencil/core";

import {
  StzhDialogOpenEvent,
  StzhDialogCloseEvent
} from "../../index";

import { setPropsIfNull } from "../../utils/utils";
import { createBaseFocusTrapOptions } from "../../utils/overlay-utils";

import { StzhDialogLocalizedText } from "./stzh-dialog.localization"

import { createFocusTrap, FocusTrap } from 'focus-trap';

const CLASS_BODY_OPEN = "stzh-dialog-open";

let activeDialog: StzhDialog = null;
let dialogCounter = 0;

/**
 * @slot actions - Slot for actions element
 * @slot - Slot for any content inside the dialog
 */
@Component({
  tag: "stzh-dialog",
  styleUrl: "stzh-dialog.scss",
  scoped: true
})
export class StzhDialog {
  /** Translation strings */
  @Prop() localization: StzhDialogLocalizedText;

  /** Close dialog when pressing ESC */
  @Prop() closeOnEscapeKey: boolean = false;

  /** Close dialog when clicking outside of it */
  @Prop() closeOnBackdropClick: boolean = false;

  /** Open status */
  @Prop({ mutable: true }) open: boolean = false;

  /** Stay in original position (true) or move to body (false) */
  @Prop() stay: boolean = true;

  /** Size */
  @Prop({ reflect: true }) size: "default" | "small" = "default";

  /** Whether to hide close button */
  @Prop() hideClose: boolean = false;

  /** Heading text
   * *mandatory* since DESI 4.0
   * */
  @Prop() heading: string = "";

  /** Accessibility role */
  @Prop({ attribute: "a11y-role" }) a11yRole: "dialog" | "alertdialog" = "alertdialog";

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the close button element.
   * Default value will be taken from translations.
   */
  @Prop() closeAnalyticsId: string;

  /** Dialog open event */
  @Event() stzhOpen: EventEmitter<StzhDialogOpenEvent>;

  /** Dialog close event */
  @Event() stzhClose: EventEmitter<StzhDialogCloseEvent>;

  @Element() element: HTMLStzhDialogElement;

  @Listen("click", { target: "document", capture: true })
  handleOutsideClick(event: MouseEvent) {
    if (!this.open || !this.closeOnBackdropClick) {
      return
    }

    const isClickOutside = event.target !== this.dialogElement
      && this.dialogElement.contains(event.target as HTMLElement) === false

    if (isClickOutside) {
      this.hide()
    }
  }

  @Listen("keydown", { target: "document" })
  handleKeydown(event: KeyboardEvent) {
    if (
      event.key === "Escape"
      && activeDialog === this
      && this.closeOnEscapeKey
    ) {
      this.hide();
    }
  }

  @Listen("click", { target: "document" })
  handleOpenTriggerClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    if (target.dataset.stzhDialogTrigger === this.element.id) {
      this.show();
    } else {
      const closest = target.closest("[data-stzh-dialog-trigger]") as HTMLElement;

      if (closest && closest.dataset.stzhDialogTrigger === this.element.id) {
        this.show();
      }
    }
  }

  @Method()
  async show() {
    this.open = true;
    this.stzhOpen.emit({
      component: "stzh-dialog"
    });
  }

  @Method()
  async hide() {
    this.open = false;
    this.stzhClose.emit({
      component: "stzh-dialog"
    });
  }

  private observer: MutationObserver;
  private id: string;
  private trap: FocusTrap;
  private parentElement: HTMLElement;
  private dialogElement: HTMLDivElement;

  private handleDialogClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.dataset.stzhDialogClose) {
      this.hide();
    } else {
      const closest = target.closest("[data-stzh-dialog-close]");

      if (closest) {
        this.hide();
      }
    }
  }

  private handleCloseButtonClick = () => {
    this.hide();
  }

  private init = () => {
    const actions = this.element.querySelector('stzh-actions[slot="actions"]');

    if (actions) {
      setPropsIfNull(actions, {
        variant: "dialog"
      } as HTMLStzhActionsElement)
    }
  }

  private getSiblings() {
    if (!this.parentElement) {
      return [];
    }

    return Array.from(this.parentElement.children).filter(
      (child) => child !== this.element
    );
  }

  private disableSiblings() {
    this.getSiblings().forEach((sibling) => {
      sibling.setAttribute("aria-hidden", "true");
    });
  }

  private enableSiblings() {
    this.getSiblings().forEach((sibling) => {
      sibling.removeAttribute("aria-hidden");
    });
  }

  private dialogShown() {
    activeDialog = this;
    document.body.classList.add(CLASS_BODY_OPEN);
    this.disableSiblings();

    if (this.trap) {
      this.trap.activate();
    }
  }

  private dialogHidden() {
    document.body.classList.remove(CLASS_BODY_OPEN);
    this.enableSiblings();

    if (this.trap) {
      this.trap.deactivate();
    }

    activeDialog = null;
  }

  async componentWillLoad() {
    this.id = `stzh-dialog-${dialogCounter}`;
    dialogCounter++;

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "dialog");
    }
  }

  componentDidRender() {
    this.open ? this.dialogShown() : this.dialogHidden();
  }

  componentDidUpdate() {
    this.trap.updateContainerElements(this.element);
  }

  componentDidLoad() {
    this.trap = createFocusTrap(this.element, {
      fallbackFocus: this.dialogElement,
      ...createBaseFocusTrapOptions()
    });

    if (this.open) {
      this.dialogShown();
    }
  }

  connectedCallback() {
    if (!this.stay && this.element.parentElement !== document.body) {
      document.body.appendChild(this.element);
    }

    this.parentElement = this.element.parentElement;

    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true
    });
  }

  disconnectedCallback() {
    this.dialogHidden();

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render() {
    return (
      <Host>
        <div
          class={{
            "stzh-dialog": true,
            "stzh-dialog--is-open": this.open,
            "stzh-dialog--has-heading": !!this.heading,
            "stzh-dialog--has-close-hidden": this.hideClose,
            [`stzh-dialog--size-${this.size}`]: !!this.size,
          }}
        >
          <div class="stzh-dialog__backdrop"></div>
          <div class="stzh-dialog__content">
            <div
              class="stzh-dialog__dialog"
              ref={(el) => (this.dialogElement = el as HTMLDivElement)}
              tabindex="-1"
              role={this.a11yRole}
              aria-hidden={this.open ? "false" : "true"}
              onClick={this.handleDialogClick}
            >
                <stzh-heading
                  id={`${this.id}-heading`}
                  class="stzh-dialog__heading"
                  level="2"
                >
                  {this.heading ? this.heading : 'Title'}
                </stzh-heading>

              <div class="stzh-dialog__dialog-content">
                <slot></slot>
              </div>

              <div class="stzh-dialog__actions">
                <slot name="actions"></slot>
              </div>

              {!this.hideClose &&
                <stzh-button
                  class="stzh-dialog__close-button"
                  variant="tertiary"
                  size="small"
                  icon="close"
                  iconOnly={true}
                  onClick={this.handleCloseButtonClick}
                  a11yLabel={this.localization.close}
                  analyticsId={this.closeAnalyticsId || this.localization.close}
                ></stzh-button>
              }
            </div>
          </div>
        </div>
      </Host>
    );
  }
}

